import { Skeleton } from "@/components/ui/skeleton"

/**
 * OKS-560 — Loading fallback for the §21 re-engagement screen (UX Spec §3).
 * Mirrors the populated column silhouette (hero band → AggregateCard cluster +
 * text → one MissionCard) rather than a spinner, reusing the
 * `first-mission-skeleton` cover-block pattern.
 *
 * `<Skeleton>` uses `animate-pulse`, which Tailwind pauses under
 * `prefers-reduced-motion` — reduced-motion is honored without extra wiring.
 *
 * # Locked testid
 *   - `welcome-back-skeleton` — wrapper
 */
export function WelcomeBackSkeleton() {
  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid="welcome-back-skeleton"
      aria-busy="true"
    >
      {/* Warm hero band */}
      <div className="bg-accent/40 flex flex-col items-center gap-2 rounded-xl py-6">
        <Skeleton className="size-10 rounded-full" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
      {/* AggregateCard: avatar cluster dots + one text bar */}
      <div className="bg-accent/10 flex flex-col items-center gap-3 rounded-xl py-6">
        <div className="flex -space-x-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              className="border-background size-10 rounded-full border-2"
            />
          ))}
        </div>
        <Skeleton className="h-4 w-3/4" />
      </div>
      {/* MissionCard: cover block + title + CTA bar */}
      <div className="overflow-clip rounded-xl border">
        <Skeleton className="aspect-[16/9] w-full rounded-none" />
        <div className="flex flex-col gap-2.5 px-4 pt-3.5 pb-4">
          <Skeleton className="h-4 w-3/4" />
          {/* OKS-938 — value row (deadline/format left, reward chips right)
              then the two bonus lines, so the skeleton matches the resolved
              card's height and nothing shifts on load (UX Spec §5 Loading). */}
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-3 w-1/2" />
            <div className="flex shrink-0 items-center gap-1">
              <Skeleton className="h-5 w-14 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </div>
          <Skeleton className="h-3 w-3/5" />
          <Skeleton className="h-3 w-2/5" />
          <Skeleton className="h-11 w-full rounded-md" />
        </div>
      </div>
    </div>
  )
}

export default WelcomeBackSkeleton
